/**
 * SystemToolsContent - System 页面的 Tools Tab
 *
 * 与 Component 页面的 ToolsContent 不同，这里使用 useEnvironments hook
 * 获取多环境信息，传递给多环境版工具组件。
 */
import React, { useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  makeStyles,
  FormControl,
  Select,
  MenuItem,
} from '@material-ui/core';
import LockIcon from '@material-ui/icons/Lock';
import { SystemSealedSecretTool } from './SystemSealedSecretTool';
import { useEnvironments } from '@internal/plugin-kubernetes-workloads';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    minHeight: 600,
    display: 'flex',
    flexDirection: 'column' as const,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  headerIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  headerTitle: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  toolSelect: {
    minWidth: 200,
  },
  selectMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  selectIcon: {
    display: 'flex',
    alignItems: 'center',
    '& svg': { fontSize: '1.2rem' },
  },
  content: {
    flex: 1,
    padding: theme.spacing(3),
    overflow: 'auto',
  },
}));

interface ToolDefinition {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
}

const tools: ToolDefinition[] = [
  {
    id: 'sealed-secret',
    name: 'SealedSecret 加密',
    description: '将敏感数据加密为 SealedSecret 格式（多环境支持）',
    icon: <LockIcon />,
  },
];

export const SystemToolsContent: React.FC = () => {
  const classes = useStyles();
  const [selectedTool, setSelectedTool] = useState('sealed-secret');
  const { environments, loading: envsLoading } = useEnvironments();

  const selectedToolDef = tools.find(t => t.id === selectedTool);

  // 转换为 SystemSealedSecretTool 需要的格式
  const envOptions = environments.map(env => ({
    label: env.label || env.name,
    cluster: env.cluster,
    namespace: env.namespace,
  }));

  return (
    <Paper className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Box className={classes.headerIcon}>
            {selectedToolDef?.icon || <LockIcon />}
          </Box>
          <Box className={classes.headerTitle}>
            <Typography variant="h6">
              {selectedToolDef?.name || '开发工具'}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {selectedToolDef?.description || '选择一个工具开始使用'}
            </Typography>
          </Box>
        </Box>

        <FormControl variant="outlined" size="small" className={classes.toolSelect}>
          <Select
            value={selectedTool}
            onChange={(e) => setSelectedTool(e.target.value as string)}
          >
            {tools.map(tool => (
              <MenuItem key={tool.id} value={tool.id}>
                <Box className={classes.selectMenuItem}>
                  <span className={classes.selectIcon}>{tool.icon}</span>
                  <span>{tool.name}</span>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box className={classes.content}>
        {envsLoading ? (
          <Typography variant="body2" color="textSecondary">加载环境信息...</Typography>
        ) : selectedTool === 'sealed-secret' ? (
          <SystemSealedSecretTool environments={envOptions} />
        ) : null}
      </Box>
    </Paper>
  );
};

export default SystemToolsContent;
